import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useScrollSpy
 * Drives a vertical-nav + scrollable-content pattern: tracks which section
 * is currently in view inside a scrollable container, and exposes a
 * `scrollToSection` function for click-to-jump nav items.
 *
 * @param {string[]} sectionIds - ids of sections to observe, in order.
 * @param {object} [options]
 * @param {string} [options.rootMargin] - IntersectionObserver rootMargin;
 *        controls how big the "active" trigger band at the top of the
 *        container is. Default treats the top ~70% as active.
 * @param {number} [options.clickScrollSuppressMs] - how long to ignore the
 *        observer after a click-triggered scroll, so the clicked item stays
 *        highlighted during the smooth-scroll animation instead of
 *        flickering through whatever it passes over.
 *
 * @returns {{
 *   activeId: string,
 *   setActiveId: (id: string) => void,
 *   containerRef: React.RefObject,
 *   registerSectionRef: (id: string) => (el: HTMLElement | null) => void,
 *   scrollToSection: (id: string) => void,
 * }}
 */
export function useScrollSpy(sectionIds, options = {}) {
  const { rootMargin = "0px 0px -70% 0px", clickScrollSuppressMs = 600 } = options;

  const [activeId, setActiveId] = useState(sectionIds[0] ?? null);

  const containerRef = useRef(null);
  const sectionRefs = useRef({});
  // Guards the observer from fighting a programmatic scroll triggered by a click.
  const isClickScrollingRef = useRef(false);
  const clickScrollTimeoutRef = useRef(null);

  // Stable setter so callers/components can re-sync activeId (e.g. when
  // the underlying data/sections change) without re-deriving the hook.
  const registerSectionRef = useCallback(
    (id) => (el) => {
      if (el) sectionRefs.current[id] = el;
      else delete sectionRefs.current[id];
    },
    []
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || sectionIds.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrollingRef.current) return;

        // Pick the entry closest to the top of the scroll container among
        // those currently intersecting, so the active id updates as soon
        // as a new section's heading crosses into the "active band".
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const id = visible[0].target.getAttribute("data-section-id");
          setActiveId(id);
        }
      },
      { root: container, rootMargin, threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds.join("|"), rootMargin]);

  const scrollToSection = useCallback(
    (id) => {
      const el = sectionRefs.current[id];
      const container = containerRef.current;
      if (!el || !container) return;

      isClickScrollingRef.current = true;
      setActiveId(id);
      el.scrollIntoView({ behavior: "smooth", block: "start" });

      clearTimeout(clickScrollTimeoutRef.current);
      clickScrollTimeoutRef.current = setTimeout(() => {
        isClickScrollingRef.current = false;
      }, clickScrollSuppressMs);
    },
    [clickScrollSuppressMs]
  );

  useEffect(() => () => clearTimeout(clickScrollTimeoutRef.current), []);

  return { activeId, setActiveId, containerRef, registerSectionRef, scrollToSection };
}

export default useScrollSpy;